<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 md:py-10">
    <div
      class="bg-white md:rounded-xl shadow-2xl w-full max-w-screen-lg min-w-[340px] max-h-full p-0 relative flex flex-col overflow-hidden">
      <!-- Modal Headline -->
      <div class="w-full px-8 pt-8 pb-2">
        <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 class="text-2xl font-bold text-gray-800">
            {{ localUser?.isNew ? 'Neuer Mitarbeiter' : (localUser?.name || '') + ' ' + (localUser?.surname || '') }}
          </h2>
          <span v-if="localUser?.id && !localUser?.isNew" class="text-xs font-medium text-gray-500">
            Zuletzt aktiv: {{ formatLastSeen(localUser.lastSeen) }}
          </span>
        </div>
        <div v-if="localUser?.pendingApproval && isAdmin"
          class="mt-3 flex flex-wrap items-center gap-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3">
          <span class="flex-1 text-sm font-semibold text-amber-800">
            <i class="fa fa-exclamation-triangle mr-2"></i>
            Freigabe ausstehend<template v-if="localUser.pendingApprovalBy"> — geändert von {{ localUser.pendingApprovalBy }}</template>.
            Der Mitarbeiter ist für neue Aufträge gesperrt.
          </span>
          <button type="button" class="admin-button--primary" :disabled="approvingEmployee" @click="approveEmployee">
            {{ approvingEmployee ? 'Freigabe läuft…' : 'Freigeben' }}
          </button>
        </div>
        <div v-else-if="localUser?.pendingApproval && !isAdmin"
          class="mt-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          Änderungen müssen erst von einem internen Admin freigegeben werden.
        </div>
        <div v-if="localUser?.isNew" class="mt-3 flex items-center gap-3">
          <label class="font-semibold">Stammdaten kopieren von:</label>
          <select v-model="copyFromEmployeeId" @change="copyEmployeeData" class="border rounded px-3 py-2">
            <option value="">-- Mitarbeiter auswählen --</option>
            <option v-for="emp in allEmployees" :key="emp.id" :value="emp.id">
              {{ emp.persnr }} - {{ emp.name }} {{ emp.surname }}
            </option>
          </select>
        </div>
      </div>
      <!-- Main scrollable content -->
      <div class="flex-1 overflow-y-auto p-8 pt-4 pb-12">

        <button
          class="absolute top-4 right-6 text-gray-700 hover:text-black text-3xl p-0 bg-transparent border-0 outline-none admin-button--ghost admin-button--icon"
          aria-label="Schließen" title="Schließen"
          @click="$emit('close')">
          <i class="fa fa-xmark"></i>
        </button>
        <div class="flex flex-row items-start gap-8">
          <!-- Form Sections (2 columns where reasonable) -->
          <div class="flex-1">
            <!-- Persönliche Angaben -->
            <div class="mb-8">
              <h3 class="font-bold text-lg mb-2 inline-block mr-4">Persönliche Angaben</h3><button
                class="bg-blue-500 text-white rounded admin-button--primary" @click="saveUser">Alles speichern</button>
              <div class="flex flex-col md:flex-row gap-6 items-start">
                <div class="flex-1 flex flex-col gap-6 w-full">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-semibold mb-1">Vorname</label>
                      <input v-model="localUser.name" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Nachname</label>
                      <input v-model="localUser.surname" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Straße</label>
                      <input v-model="localUser.street" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Hausnummer</label>
                      <input v-model="localUser.houseNumber" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">PLZ</label>
                      <input v-model="localUser.zip" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Stadt</label>
                      <input v-model="localUser.city" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Telefon</label>
                      <input v-model="localUser.phone" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">E-Mail</label>
                      <input v-model="localUser.email" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">KFZ-Kennzeichen</label>
                      <input v-model="localUser.kennzeichen" class="border rounded px-3 py-2 w-full" />
                    </div>
                  </div>
                </div>
                <div class="flex-shrink-0 flex items-center justify-center md:ml-6">
                  <div @click="triggerProfilePicInput" class="cursor-pointer group relative">
                    <img v-if="localUser && localUser.picture" :src="localUser.picture.data"
                      class="w-64 object-cover shadow border-2 border-gray-300 group-hover:opacity-70 transition"
                      style="height: 350px;" />
                    <div v-else
                      class="w-64 bg-gray-200 flex items-center justify-center border-2 border-gray-300 group-hover:opacity-70 transition"
                      style="height: 350px;">
                      <i class="fa fa-user text-5xl text-gray-400"></i>
                    </div>
                    <input ref="profilePicInput" type="file" class="hidden" accept="image/*"
                      @change="handlePictureUpload" />
                    <span
                      class="absolute inset-0 flex items-center justify-center text-gray-600 opacity-0 group-hover:opacity-100 pointer-events-none transition">Profilbild
                      ändern</span>
                  </div>
                </div>
              </div>
            </div>


            <!-- Dokumente -->
            <div class="mt-8">
              <!-- Tab Bar -->
              <div class="flex flex-wrap gap-2 border-b mb-4">
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Beschaeftigungsverhaeltnis' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="activeTab = 'Beschaeftigungsverhaeltnis'">
                  Beschäftigungsverhältnis
                </button>
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'NotwendigeDokumente' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="activeTab = 'NotwendigeDokumente'">
                  Notwendige Dokumente
                </button>
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Dokumente' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="activeTab = 'Dokumente'">
                  Dokumente
                </button>
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Material' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="openMaterialTab">
                  Material
                </button>
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Trainings' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="activeTab = 'Trainings'">
                  Schulungen
                </button>
                <button v-if="!localUser?.is_external" class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Urlaubsplanung' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="openVacationTab">
                  Urlaubsplanung
                </button>
                <button v-if="!localUser?.is_external" class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Zeitkonto' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="openTimeAccountTab">
                  Zeitkonto
                </button>
                <button v-if="isAdmin && !localUser?.isNew" class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Sperrungen' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="openBlocksTab">
                  Sperrungen
                </button>
                <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                  :class="activeTab === 'Meldeliste' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                  @click="activeTab = 'Meldeliste'">
                  Meldeliste
                </button>
              </div>
              <!-- Tab Content -->
              <div v-if="activeTab === 'Lohn'">
                <div v-if="lohnFiles.length" class="mb-4">
                  <div class="overflow-y-auto max-h-96">
                    <div v-for="file in lohnFiles" :key="file.id"
                      class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                      <span class="truncate">{{ formatLohnFilename(file.name) }}</span>
                      <button
                        class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                        @click="viewDocument(file)">
                        <i class="fa fa-eye mr-1"></i> Ansehen
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-gray-500 text-center py-8">
                  <span>Keine Lohn-Dokumente vorhanden.</span>
                </div>
              </div>
              <div v-else-if="activeTab === 'Qualifikation'">
                <div v-if="qualifikationFiles.length" class="mb-4">
                  <div class="overflow-y-auto max-h-96">
                    <div v-for="file in qualifikationFiles" :key="file.id"
                      class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                      <span class="truncate">{{ file.name }}</span>
                      <div class="flex gap-2">
                        <button
                          class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                          @click="viewDocument(file)">
                          <i class="fa fa-eye mr-1"></i> Ansehen
                        </button>
                        <button
                          class="ml-4 px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 transition text-sm font-semibold admin-button--danger admin-button--sm"
                          @click="deleteDocument(file)">
                          <i class="fa fa-trash mr-1"></i> Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-gray-500 text-center py-8">
                  <span>Keine Qualifikation-Dokumente vorhanden.</span>
                </div>
              </div>
              <div v-else-if="activeTab === 'Schichten'">
                <div v-if="assignedDocuments.length" class="mb-4">
                  <div class="overflow-y-auto max-h-96">
                    <div v-for="doc in assignedDocuments" :key="doc.id"
                      class="flex flex-col md:flex-row md:items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                      <div class="flex-1 min-w-0">
                        <div class="font-semibold">{{ doc.contract }}</div>
                        <div class="text-xs text-gray-600 truncate">Job: {{ doc.jobname }}</div>
                        <div class="text-xs text-gray-600 truncate">Ort: {{ doc.location }}</div>
                        <div class="text-xs mt-1" :class="doc.signed ? 'text-green-600' : 'text-red-600'">
                          {{ doc.signed ? 'Signiert' : 'Nicht signiert' }}
                        </div>
                      </div>
                      <button
                        class="mt-2 md:mt-0 md:ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                        @click="viewJobDocument(doc)">
                        <i class="fa fa-eye mr-1"></i> Schichtnachweis anzeigen
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-gray-500 text-center py-8">
                  <span>Keine Schichtnachweise vorhanden.</span>
                </div>
              </div>
              <div v-else-if="activeTab === 'Beschaeftigungsverhaeltnis'">
                <div class="mb-8">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-semibold mb-1">Personalnummer</label>
                      <div class="flex gap-2">
                        <select v-if="localUser?.isNew && !isExternalDispo" v-model="persnrPrefix" @change="applyPersnrSuggestion"
                          class="border rounded px-3 py-2">
                          <option value="CSP">CSP</option>
                          <option value="SUB">SUB</option>
                        </select>
                        <input v-model="localUser.persnr" @input="validatePersnr"
                          class="border rounded px-3 py-2 w-full" :class="{'border-red-500': persnrError}"
                          :disabled="!localUser?.isNew && !isSuperadmin" />
                      </div>
                      <span v-if="isExternalDispo && firmPersnrPrefix" class="text-gray-500 text-xs mt-1 block">
                        Muss mit „{{ firmPersnrPrefix }}" beginnen.
                      </span>
                      <span v-if="persnrError" class="text-red-600 text-sm font-semibold mt-1 block">{{ persnrError }}</span>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Qualifikation</label>
                      <select v-model="localUser.Qualifikation" class="border rounded px-3 py-2 w-full">
                        <option value="1">VA-Bewachung (1)</option>
                        <option value="2">VA-Normal (2)</option>
                        <option value="3">VA-Top (3)</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Funktion</label>
                      <input v-model="localUser.function" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">ID-BEWAR</label>
                      <input v-model="localUser.idbewar" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Eintrittsdatum</label>
                      <input v-model="localUser.entryDate" type="date" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Austrittsdatum</label>
                      <input v-model="localUser.exitDate" @change="updateActiveStatus" type="date"
                        class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Dienstausweisnummer</label>
                      <input v-model="localUser.idnr" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">QR-Text</label>
                      <input v-model="localUser.qrValue" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Monatliche Stunden</label>
                      <input v-model="localUser.monthlyHours" type="number" min="0"
                        class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Urlaubstage aktuelles Jahr</label>
                      <input v-model.number="localUser.vacationDaysThisYear" type="number" min="0" step="0.5"
                        class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Urlaubstage Vorjahr</label>
                      <input v-model.number="localUser.vacationDaysLastYear" type="number" min="0" step="0.5"
                        class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Sonstiger genommener Urlaub (außerhalb der App)</label>
                      <input v-model.number="localUser.vacationDaysTakenOutside" type="number" min="0" step="0.5"
                        class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <input type="checkbox" id="minijob" v-model="localUser.minijob" class="hidden" />
                      <label for="minijob" class="cursor-pointer z-0">Geringfügig Beschäftigt</label>
                    </div>
                    <div :class="{'bg-yellow-100 border-2 border-yellow-500 p-2 rounded': activeAutoUpdated}">
                      <input type="checkbox" id="appaktiv" v-model="localUser.active" class="hidden" @change="activeManuallyChanged = true" />
                      <label for="appaktiv" class="cursor-pointer z-0">Mitarbeiter aktiv</label>
                      <span v-if="activeAutoUpdated" class="ml-2 text-yellow-700 text-sm font-semibold">
                        <i class="fa fa-exclamation-triangle mr-1"></i>Automatisch angepasst
                      </span>
                    </div>
                    <div>
                      <input type="checkbox" id="isexternal" v-model="localUser.is_external" class="hidden"
                        :disabled="isExternalDispo" />
                      <label for="isexternal" class="cursor-pointer z-0">Externer Mitarbeiter (Fremdfirma)</label>
                    </div>
                    <div v-if="localUser.is_external">
                      <label class="block text-sm font-semibold mb-1">Fremdfirma</label>
                      <select v-model="localUser.Firmenkurzel" :disabled="isExternalDispo"
                        class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option v-for="k in subunternehmer" :key="k.id" :value="k.firmenkuerzel">
                          {{ k.unternehmensname }} ({{ k.firmenkuerzel }})
                        </option>
                        <option v-if="localUser.Firmenkurzel && !matchedFremdfirma" :value="localUser.Firmenkurzel" disabled>
                          {{ localUser.Firmenkurzel }} (nicht gefunden)
                        </option>
                      </select>
                      <span v-if="fremdfirmaWarning" class="text-yellow-700 text-sm font-semibold mt-1 block">
                        <i class="fa fa-exclamation-triangle mr-1"></i>{{ fremdfirmaWarning }}
                      </span>
                    </div>
                    <div>
                      <input id="einsatzfaehig" type="checkbox" :checked="einsatzfaehig" disabled
                        class="disabled" />
                      <label for="einsatzfaehig" class="cursor-pointer z-0">Einsatzfähig</label>
                    </div>
                    <div>
                      <input id="einsatzbereitManuell" type="checkbox" v-model="localUser.einsatzbereitManuell" />
                      <label for="einsatzbereitManuell" class="cursor-pointer z-0">
                        Einsatzbereit (manuell)
                      </label>
                    </div>
                  </div>
                  <div v-if="!localUser?.isNew && localUser?.id" class="mt-6">
                    <button type="button"
                      class="bg-blue-500 text-white rounded px-4 py-2 admin-button--primary"
                      @click="printBadge">
                      <i class="fa fa-print mr-1"></i> Ausweis drucken
                    </button>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Meldeliste'">
                <div class="mb-8">
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-semibold mb-1">Geburtsname</label>
                      <input v-model="localUser.birthName" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Frühere Namen</label>
                      <input v-model="localUser.formerNames" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Geburtsort</label>
                      <input v-model="localUser.birthPlace" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Geburtsland</label>
                      <input v-model="localUser.birthCountry" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Geschlecht</label>
                      <select v-model="localUser.gender" class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option value="Männlich">Männlich</option>
                        <option value="Weiblich">Weiblich</option>
                        <option value="Divers">Divers</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Hausnummer</label>
                      <input v-model="localUser.houseNumber" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Bundesland</label>
                      <input v-model="localUser.state" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Land</label>
                      <input v-model="localUser.country" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Nationalität</label>
                      <input v-model="localUser.nationality" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Ausweisart</label>
                      <select v-model="localUser.idType" class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option value="Personalausweis">Personalausweis</option>
                        <option value="Arbeitserlaubnis/Aufenthaltstitel/Pass">Arbeitserlaubnis/Aufenthaltstitel/Pass</option>
                        <option value="Reisepass">Reisepass</option>
                        <option value="Sonstige">Sonstige</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Ausweisart genau</label>
                      <input v-model="localUser.idTypeDetail" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Ausweisnummer</label>
                      <input v-model="localUser.Ausweisnr" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Ausstellende Behörde</label>
                      <input v-model="localUser.idAuthority" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Vollzeit oder Teilzeit</label>
                      <select v-model="localUser.employmentType" class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option value="normal SV-pfl. Beschäftigte">normal SV-pfl. Beschäftigte</option>
                        <option value="geringfügig Beschäftigte">geringfügig Beschäftigte</option>
                        <option value="Teilzeit">Teilzeit</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Unterrichtung gem. §34a GewO</label>
                      <select v-model="localUser.unterrichtung34a" class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option value="JA">JA</option>
                        <option value="NEIN">NEIN</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Sachkunde gem. §34a GewO</label>
                      <select v-model="localUser.sachkunde34a" class="border rounded px-3 py-2 w-full">
                        <option value="">--</option>
                        <option value="JA">JA</option>
                        <option value="NEIN">NEIN</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Andere Qualifikation</label>
                      <input v-model="localUser.otherQualification" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Wächtermeldung</label>
                      <input v-model="localUser.waechtermeldung" class="border rounded px-3 py-2 w-full" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'NotwendigeDokumente'">
                <div class="mb-8">
                  <div class="mb-4 flex items-center font-semibold">
                    <input id="required-document-german-national" type="checkbox" v-model="localUser.germanNational" />
                    <label for="required-document-german-national">
                      Deutsche Staatsangehörigkeit (Aufenthaltserlaubnis nicht erforderlich)
                    </label>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div v-for="document in requiredDocuments" :key="document.subcategory"
                      class="border rounded p-3 flex flex-col gap-2">
                      <div class="flex flex-wrap items-center justify-between gap-2">
                        <div class="flex items-center">
                          <input :id="`required-document-${document.field}`" type="checkbox"
                            v-model="localUser[document.field]"
                            @change="handleRequiredDocumentConfirmation(document)" />
                          <label :for="`required-document-${document.field}`" class="font-semibold">
                            {{ document.label }}
                          </label>
                        </div>
                        <span class="text-xs font-semibold rounded px-2 py-1"
                          :class="requiredDocumentStatus(document) === 'Fehlt' || requiredDocumentStatus(document) === 'Abgelaufen' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'">
                          {{ requiredDocumentStatus(document) }}
                        </span>
                      </div>
                      <div v-if="shouldShowManualDate(document)" class="flex items-center gap-2 text-sm">
                        <label :for="`required-document-${document.field}-manual-valid-until`">
                          Gültig bis
                        </label>
                        <input :id="`required-document-${document.field}-manual-valid-until`" type="date"
                          v-model="localUser[document.manualDateField]"
                          class="border rounded px-2 py-1" />
                      </div>
                      <div v-if="requiredDocumentFiles(document).length" class="space-y-1">
                        <div v-for="file in requiredDocumentFiles(document)" :key="file.id"
                          class="flex items-center justify-between gap-2 text-sm text-gray-600">
                          <span>
                            {{ file.name }}
                            <span v-if="file.validUntil">
                              – Gültig bis {{ formatDate(file.validUntil) }}
                              <span v-if="isRequiredDocumentExpired(file)" class="text-red-600 font-semibold">
                                (Abgelaufen)
                              </span>
                            </span>
                          </span>
                          <button type="button" class="text-blue-600 hover:text-blue-800 font-semibold"
                            @click="viewDocument(file)">
                            <i class="fa fa-eye mr-1"></i> Ansehen
                          </button>
                        </div>
                      </div>
                      <div class="flex gap-2">
                        <button type="button" :disabled="localUser.isNew || !localUser.id"
                          class="bg-blue-500 text-white rounded px-3 py-1 text-sm admin-button--primary admin-button--sm disabled:opacity-50"
                          @click="openRequiredDocumentUpload(document.subcategory)">
                          Dokument hochladen
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="mt-4 flex items-center gap-3">
                    <button v-if="!localUser.isNew" type="button"
                      class="bg-blue-500 text-white rounded px-3 py-2 admin-button--primary"
                      :disabled="documentStatusSaving" @click="saveRequiredDocumentStatus()">
                      Dokumentenstatus speichern
                    </button>
                    <span v-if="documentStatusMessage" class="text-green-600">{{ documentStatusMessage }}</span>
                    <span v-if="documentStatusError" class="text-red-600">{{ documentStatusError }}</span>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Dokumente'">
                <!-- Subgroup Tabs -->
                <div class="flex flex-wrap gap-2 border-b mb-4">
                  <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                    :class="activeDokTab === 'Personalakte' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                    @click="activeDokTab = 'Personalakte'">
                    Personalakte
                  </button>
                  <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                    :class="activeDokTab === 'Qualifikation' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                    @click="activeDokTab = 'Qualifikation'">
                    Qualifikation
                  </button>
                  <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                    :class="activeDokTab === 'Lohn' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                    @click="activeDokTab = 'Lohn'">
                    Lohn
                  </button>
                  <button class="admin-tab px-4 py-2 -mb-px font-semibold focus:outline-none bg-transparent text-black"
                    :class="activeDokTab === 'Schichten' ? 'admin-tab--active border-x-0 border-t-0 border-caspyellow font-bold' : 'border-none font-normal'"
                    @click="activeDokTab = 'Schichten'">
                    Schichten
                  </button>
                  <button :disabled="localUser.isNew || !localUser.id"
                    class="bg-blue-500 text-white rounded admin-button--primary disabled:opacity-50"
                    @click="openDocumentUpload">Dokument
                    hochladen</button>
                </div>
                <!-- Subgroup Tab Content -->
                <div v-if="activeDokTab === 'Personalakte'">
                  <div v-if="personalakteFiles.length" class="space-y-2 mb-4">
                    <div v-for="doc in personalakteFiles" :key="doc.id"
                      class="flex items-center justify-between border rounded px-3 py-2">
                      <span>{{ doc.name }}</span>
                      <div class="flex gap-2">
                        <button
                          class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                          @click="viewDocument(doc)">
                          <i class="fa fa-eye mr-1"></i> Ansehen
                        </button>
                        <button
                          class="ml-4 px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 transition text-sm font-semibold admin-button--danger admin-button--sm"
                          @click="deleteDocument(doc)">
                          <i class="fa fa-trash mr-1"></i> Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-gray-500">Keine Dokumente vorhanden.</div>
                </div>
                <div v-else-if="activeDokTab === 'Qualifikation'">
                  <div v-if="qualifikationFiles.length" class="mb-4">
                    <div class="overflow-y-auto max-h-96">
                      <div v-for="file in qualifikationFiles" :key="file.id"
                        class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                        <span class="truncate">{{ file.name }}</span>
                        <span v-if="file.validUntil" class="text-sm text-gray-600">
                          Gültig bis {{ formatDate(file.validUntil) }}
                        </span>
                        <div class="flex gap-2">
                          <button
                            class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                            @click="viewDocument(file)">
                            <i class="fa fa-eye mr-1"></i> Ansehen
                          </button>
                          <button
                            class="ml-4 px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 transition text-sm font-semibold admin-button--danger admin-button--sm"
                            @click="deleteDocument(file)">
                            <i class="fa fa-trash mr-1"></i> Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-gray-500 text-center py-8">
                    <span>Keine Qualifikation-Dokumente vorhanden.</span>
                  </div>
                </div>
                <div v-else-if="activeDokTab === 'Lohn'">
                  <div v-if="lohnFiles.length" class="mb-4">
                    <div class="overflow-y-auto max-h-96">
                      <div v-for="file in lohnFiles" :key="file.id"
                        class="flex items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                        <span class="truncate">{{ formatLohnFilename(file.name) }}</span>
                        <button
                          class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                          @click="viewDocument(file)">
                          <i class="fa fa-eye mr-1"></i> Ansehen
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-gray-500 text-center py-8">
                    <span>Keine Lohn-Dokumente vorhanden.</span>
                  </div>
                </div>
                <div v-else-if="activeDokTab === 'Schichten'">
                  <div v-if="assignedDocuments.length" class="mb-4">
                    <div class="overflow-y-auto max-h-96">
                      <div v-for="doc in assignedDocuments" :key="doc.id"
                        class="flex flex-col md:flex-row md:items-center justify-between border rounded px-3 py-2 mb-2 bg-gray-50">
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold">{{ doc.contract }}</div>
                          <div class="text-xs text-gray-600 truncate">Job: {{ doc.jobname }}</div>
                          <div class="text-xs text-gray-600 truncate">Ort: {{ doc.location }}</div>
                          <div class="text-xs mt-1" :class="doc.signed ? 'text-green-600' : 'text-red-600'">
                            {{ doc.signed ? 'Signiert' : 'Nicht signiert' }}
                          </div>
                        </div>
                        <button
                          class="mt-2 md:mt-0 md:ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                          @click="viewJobDocument(doc)">
                          <i class="fa fa-eye mr-1"></i> Schichtnachweis anzeigen
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-gray-500 text-center py-8">
                    <span>Keine Schichtnachweise vorhanden.</span>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Material'">
                <div v-if="materialLoading" class="text-center py-8 text-gray-500">
                  <span>Lade Material ...</span>
                </div>
                <div v-else>
                  <div class="overflow-y-auto max-h-96">
                    <div v-for="(mat, idx) in materials" :key="mat.id"
                      class="flex items-center gap-2 border rounded px-3 py-2 mb-2 bg-gray-50">
                      <input v-model="mat.editName" @input="onMaterialEdit(idx)"
                        class="border rounded px-2 py-1 flex-1" />
                      <button v-if="mat.editDirty"
                        class="px-2 py-1 bg-green-500 text-white rounded text-xs font-semibold admin-button--primary admin-button--sm"
                        @click="saveMaterial(idx)">
                        Speichern
                      </button>
                      <button class="px-2 py-1 bg-red-500 text-white rounded text-xs font-semibold admin-button--danger admin-button--sm"
                        @click="deleteMaterial(idx)">
                        Löschen
                      </button>
                    </div>
                    <!-- Add new material row -->
                    <div class="flex items-center gap-2 border rounded px-3 py-2 bg-gray-100">
                      <select v-model="newMaterialName" class="border rounded px-2 py-1 flex-1">
                        <option value="">-- Wählen Sie aus --</option>
                        <option v-for="eq in equipment" :key="eq.id" :value="eq.name">{{ eq.name }}</option>
                      </select>
                      <button class="px-2 py-1 bg-blue-500 text-white rounded text-xs font-semibold admin-button--primary admin-button--sm"
                        :disabled="!newMaterialName.trim()" @click="addMaterial">
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Trainings'">
                <div class="mb-4">
                  <div class="overflow-y-auto max-h-96">
                    <table class="w-full border">
                      <thead class="">
                        <tr>
                          <th class="py-2 px-3 text-left">Aktionen</th>
                          <th class="py-2 px-3 text-left">Name</th>
                          <th class="py-2 px-3 text-left">Beschreibung</th>
                          <th class="py-2 px-3 text-left">Datum</th>
                          <th class="py-2 px-3 text-left">Stundenanzahl</th>
                          <th class="py-2 px-3 text-left">Gültig bis</th>
                          <th class="py-2 px-3 text-left">Dokument</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tr in localUser.trainings || []" :key="tr.id">
                          <td class="py-2 px-3">
                            <button
                              class="ml-4 px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 transition text-sm font-semibold admin-button--danger admin-button--sm"
                              aria-label="Schulung löschen" title="Schulung löschen"
                              @click="deleteTrainingExecution(tr)">
                              <i class="fa fa-trash"></i>
                            </button>
                          </td>

                          <td class="py-2 px-3">{{ tr.name }}</td>
                          <td class="py-2 px-3">{{ tr.description }}</td>
                          <td class="py-2 px-3">{{ formatDate(tr.date) }}</td>
                          <td class="py-2 px-3">{{ tr.duration }}</td>
                          <td class="py-2 px-3">{{ formatDate(tr.validUntil) }}</td>
                          <td class="py-2 px-3">
                            <button v-if="tr.document"
                              class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                              @click="viewDocument({ id: tr.document })">
                              <i class="fa fa-eye mr-1"></i> Ansehen
                            </button>
                            <span v-else class="text-gray-400">-</span>
                          </td>
                        </tr>
                        <tr v-if="!localUser.trainings || !localUser.trainings.length">
                          <td colspan="4" class="text-center text-gray-500 py-4">Keine Schulungen vorhanden.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Urlaubsplanung'">
                <div class="mb-6">
                  <h4 class="font-bold text-md mb-3">Urlaubsstand</h4>
                  <div v-if="vacationBalanceChanged"
                    class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-3 mb-4">
                    <p class="font-bold"><i class="fa fa-exclamation-triangle mr-2"></i>Achtung!</p>
                    <p>Änderungen am Urlaubsstand sollten gut überlegt sein, da sie die Urlaubsberechnung beeinflussen.
                    </p>
                  </div>
                  <div v-if="successMessage" class="bg-green-100 border-l-4 border-green-500 text-green-700 p-3 mb-4">
                    <p class="font-bold"><i class="fa fa-check-circle mr-2"></i>{{ successMessage }}</p>
                  </div>
                  <div class="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label class="block text-sm font-semibold mb-1">Jahresurlaub (Stand 1.1.)</label>
                      <input v-model.number="localUser.vacationDaysThisYear" @input="vacationBalanceChanged = true"
                        type="number" min="0" step="0.5" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Resturlaub Vorjahr</label>
                      <input v-model.number="localUser.vacationDaysLastYear" @input="vacationBalanceChanged = true"
                        type="number" min="0" step="0.5" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Sonstiger genommener Urlaub (außerhalb der App)</label>
                      <input v-model.number="localUser.vacationDaysTakenOutside" @input="vacationBalanceChanged = true"
                        type="number" min="0" step="0.5" class="border rounded px-3 py-2 w-full" />
                    </div>
                  </div>
                  <button @click="saveVacationBalance" :disabled="!vacationBalanceChanged"
                    class="px-4 py-2 rounded text-white font-semibold admin-button--primary"
                    :class="vacationBalanceChanged ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'">
                    <i class="fa fa-save mr-2"></i>Urlaubsstand speichern
                  </button>
                </div>

                <div class="mb-6">
                  <h4 class="font-bold text-md mb-3">Genommener Urlaub</h4>
                  <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 text-center">
                    <div class="bg-blue-50 border border-blue-200 rounded p-3">
                      <div class="text-sm text-gray-600">Resturlaub (bis 31.03.)</div>
                      <div class="text-2xl font-bold text-blue-600">{{ vacationStats.vacationFromLastYearTaken }} Tage
                      </div>
                    </div>
                    <div class="bg-green-50 border border-green-200 rounded p-3">
                      <div class="text-sm text-gray-600">Regulärer Urlaub</div>
                      <div class="text-2xl font-bold text-green-600">{{ vacationStats.regularVacationTaken }} Tage</div>
                    </div>
                    <div class="bg-orange-50 border border-orange-200 rounded p-3">
                      <div class="text-sm text-gray-600">Sonstiger Urlaub (außerhalb der App)</div>
                      <div class="text-2xl font-bold text-orange-600">{{ vacationStats.vacationTakenOutside }} Tage</div>
                    </div>
                    <div class="bg-purple-50 border border-purple-200 rounded p-3">
                      <div class="text-sm text-gray-600">Geplant</div>
                      <div class="text-2xl font-bold text-purple-600">{{ vacationStats.plannedDays }} Tage</div>
                    </div>
                  </div>
                  <div class="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
                    <div class="text-sm text-gray-600">Verbleibender Urlaub</div>
                    <div class="text-2xl font-bold text-gray-800">{{ vacationStats.remainingDays }} Tage</div>
                  </div>
                </div>

                <div class="mb-4">
                  <h4 class="font-bold text-md mb-3">Urlaubstage</h4>
                  <div v-if="vacationDays.length" class="overflow-y-auto max-h-96">
                    <table class="w-full border">
                      <thead class="bg-caspgrey">
                        <tr>
                          <th class="py-2 px-3 text-left">Datum</th>
                          <th class="py-2 px-3 text-left">Von</th>
                          <th class="py-2 px-3 text-left">Bis</th>
                          <th class="py-2 px-3 text-left">Stunden</th>
                          <th class="py-2 px-3 text-left">Status</th>
                          <th class="py-2 px-3 text-left">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(day, idx) in vacationDays" :key="idx" class="border-b">
                          <td class="py-2 px-3">{{ formatDate(day.date) }}</td>
                          <td class="py-2 px-3">{{ day.startTime }}</td>
                          <td class="py-2 px-3">{{ day.endTime }}</td>
                          <td class="py-2 px-3">{{ day.hours }}</td>
                          <td class="py-2 px-3">
                            <span v-if="day.isFuture" class="text-purple-600 font-semibold">Geplant</span>
                            <span v-else-if="day.isBeforeMarch31 && idx < localUser.vacationDaysLastYear"
                              class="text-blue-600 font-semibold">Genommen (Resturlaub)</span>
                            <span v-else class="text-green-600 font-semibold">Genommen (Jahresurlaub)</span>
                          </td>
                          <td class="py-2 px-3">
                            <button @click="calculateHoursForJob(day.id)"
                              class="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold admin-button--secondary admin-button--sm">
                              <i class="fa fa-calculator mr-1"></i>Stunden berechnen
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-gray-500 text-center py-8">
                    <span>Keine Urlaubstage vorhanden.</span>
                  </div>
                </div>

                <div class="mb-6">
                  <h4 class="font-bold text-md mb-3">Urlaubstage aus Vorjahr (zur Information)</h4>
                  <div class="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                    <p class="text-sm text-blue-800">
                      <i class="fa fa-info-circle mr-2"></i>
                      Die folgenden Urlaubstage aus dem Vorjahr werden hier nur zur Information angezeigt und fließen
                      nicht in die aktuellen Berechnungen ein.
                    </p>
                  </div>
                  <div v-if="previousYearJobs.length" class="overflow-y-auto max-h-96">
                    <table class="w-full border">
                      <thead class="bg-caspgrey">
                        <tr>
                          <th class="py-2 px-3 text-left">Datum</th>
                          <th class="py-2 px-3 text-left">Von</th>
                          <th class="py-2 px-3 text-left">Bis</th>
                          <th class="py-2 px-3 text-left">Stunden</th>
                          <th class="py-2 px-3 text-left">Status</th>
                          <th class="py-2 px-3 text-left">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(job, idx) in previousYearJobs" :key="`prev-${idx}`" class="border-b bg-gray-50">
                          <td class="py-2 px-3">{{ formatDate(job.date) }}</td>
                          <td class="py-2 px-3">{{ job.startTime }}</td>
                          <td class="py-2 px-3">{{ job.endTime }}</td>
                          <td class="py-2 px-3">{{ job.hours }}</td>
                          <td class="py-2 px-3">
                            <span class="text-gray-600 font-semibold">Vorjahr</span>
                          </td>
                          <td class="py-2 px-3">
                            <button @click="calculateHoursForJob(job.id)"
                              class="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold admin-button--secondary admin-button--sm">
                              <i class="fa fa-calculator mr-1"></i>Stunden berechnen
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-gray-500 text-center py-8">
                    <span>Keine Urlaubstage aus dem Vorjahr vorhanden.</span>
                  </div>
                </div>

                <div class="mb-4">
                  <h4 class="font-bold text-md mb-3">Aktionen</h4>
                  <div class="flex gap-4 items-end">
                    <div class="flex-1">
                      <label class="block text-sm font-semibold mb-1">Urlaubsdatum</label>
                      <input v-model="newVacationDate" type="date" class="border rounded px-3 py-2 w-full" />
                    </div>
                    <button @click="addVacation"
                      class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold admin-button--primary">
                      <i class="fa fa-plus mr-2"></i>Urlaub eintragen
                    </button>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Zeitkonto'">
                <TimeAccountTab :user="localUser" :month="selectedMonth" />
              </div>
              <div v-else-if="activeTab === 'Sperrungen'">
                <div class="mb-6">
                  <h4 class="font-bold text-md mb-3">Einsatzort-Sperren</h4>
                  <p class="text-sm text-gray-500 mb-4">
                    Gesperrte Einsatzorte verhindern, dass der Mitarbeiter dort offene Aufträge annimmt
                    oder von Disponenten eingeteilt wird — bis die Sperre manuell entfernt wird.
                  </p>
                  <div v-if="einsatzortBlocks.length" class="overflow-y-auto max-h-64 mb-6">
                    <table class="w-full border">
                      <thead class="bg-caspgrey">
                        <tr>
                          <th class="py-2 px-3 text-left">Vertrag</th>
                          <th class="py-2 px-3 text-left">Einsatzort</th>
                          <th class="py-2 px-3 text-left">Grund</th>
                          <th class="py-2 px-3 text-left">Angelegt von</th>
                          <th class="py-2 px-3 text-left">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="block in einsatzortBlocks" :key="block.id" class="border-b">
                          <td class="py-2 px-3">{{ block.contractName || block.contractCode || '—' }}</td>
                          <td class="py-2 px-3">{{ block.einsatzortName || block.einsatzortCode }}</td>
                          <td class="py-2 px-3">{{ block.reason || '—' }}</td>
                          <td class="py-2 px-3">{{ block.createdBy || '—' }}</td>
                          <td class="py-2 px-3">
                            <button type="button"
                              class="px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 text-sm font-semibold admin-button--danger admin-button--sm"
                              @click="deleteBlock(block)">
                              <i class="fa fa-trash mr-1"></i>Entfernen
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-gray-500 mb-6">Keine Sperren vorhanden.</div>

                  <h4 class="font-bold text-md mb-3">Neue Sperre</h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-semibold mb-1">Vertrag</label>
                      <select v-model="blockForm.contractCode" @change="blockForm.einsatzortCode = ''"
                        class="border rounded px-3 py-2 w-full">
                        <option value="">-- Vertrag wählen --</option>
                        <option v-for="contract in blockContracts" :key="contract.code" :value="contract.code">
                          {{ contract.name }}
                        </option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-semibold mb-1">Einsatzort</label>
                      <select v-model="blockForm.einsatzortCode" :disabled="!blockForm.contractCode"
                        class="border rounded px-3 py-2 w-full disabled:opacity-50">
                        <option value="">-- Einsatzort wählen --</option>
                        <option v-for="location in blockLocations" :key="location.code" :value="location.code">
                          {{ location.name }}
                        </option>
                      </select>
                    </div>
                    <div class="md:col-span-2">
                      <label class="block text-sm font-semibold mb-1">Grund</label>
                      <input v-model="blockForm.reason" class="border rounded px-3 py-2 w-full"
                        placeholder="z. B. Hausverbot durch den Kunden" />
                    </div>
                  </div>
                  <div class="mt-4 flex items-center gap-3">
                    <button type="button" class="admin-button--primary"
                      :disabled="!blockForm.einsatzortCode || blockSaving" @click="createBlock">
                      <i class="fa fa-ban mr-2"></i>{{ blockSaving ? 'Speichern…' : 'Sperre anlegen' }}
                    </button>
                    <span v-if="blockError" class="text-red-600 text-sm font-semibold">{{ blockError }}</span>
                  </div>
                </div>
              </div>
              <div v-else-if="activeTab === 'Personalakte'">
                <div v-if="personalakteFiles.length" class="space-y-2 mb-4">
                  <div v-for="doc in personalakteFiles" :key="doc.id"
                    class="flex items-center justify-between border rounded px-3 py-2">
                    <span>{{ doc.name }}</span>
                    <div class="flex gap-2">
                      <button
                        class="ml-4 px-3 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700 transition text-sm font-semibold admin-button--secondary admin-button--sm"
                        @click="viewDocument(doc)">
                        <i class="fa fa-eye mr-1"></i> Ansehen
                      </button>
                      <button
                        class="ml-4 px-3 py-1 bg-red-600 text-white rounded shadow hover:bg-red-700 transition text-sm font-semibold admin-button--danger admin-button--sm"
                        @click="deleteDocument(doc)">
                        <i class="fa fa-trash mr-1"></i> Löschen
                      </button>
                    </div>
                  </div>
                </div>
                <div v-else class="text-gray-500">Keine Dokumente vorhanden.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End main scrollable content -->
    </div>
  </div>
  <DocumentUploadModal v-if="showDocumentUploadModal" @close="closeDocumentUpload" :localUser="localUser"
    :initialSubcategory="documentUploadSubcategory" @updateUser="updateUserData">
  </DocumentUploadModal>
</template>




<script>
import CASP from "@/CASP.js";
import DocumentUploadModal from './DocumentUploadModal.vue';
import TimeAccountTab from '@/components/TimeAccountTab.vue';
import { isExpired, requiredDocumentStatus as getRequiredDocumentStatus } from './requiredDocumentStatus';
import { isEmployeeEinsatzfaehig } from '@/lib/employeeReadiness';
import { useCookies } from "vue3-cookies";
import { useAdminSession } from '@/admin/adminSession';
export default {
  name: 'UserProfileModal',
  components: { DocumentUploadModal, TimeAccountTab },
  props: {
    user: { type: Object, required: true }
  },
  emits: ['close', 'updateUser'],
  setup() {
    const { cookies } = useCookies();
    const { stats } = useAdminSession();
    return { cookies, sessionStats: stats };
  },
  data() {
    return {
      localUser: {},
      activeTab: 'Beschaeftigungsverhaeltnis',
      activeDokTab: 'Personalakte',
      files: [],
      assignedDocuments: [],
      pictureChanged: false,
      showDocumentUploadModal: false,
      documentUploadSubcategory: '',
      documentStatusSaving: false,
      documentStatusMessage: '',
      documentStatusError: '',
      // Material Tab
      materials: [],
      materialLoading: false,
      newMaterialName: '',
      equipment: [],
      // Vacation Tab
      vacationBalanceChanged: false,
      vacationDays: [],
      previousYearJobs: [],
      newVacationDate: '',
      successMessage: '',
      // Time Account Tab
      selectedMonth: new Date().toISOString().slice(0, 7), // YYYY-MM format
      // Active flag tracking
      activeAutoUpdated: false,
      activeManuallyChanged: false,
      // Employee creation
      copyFromEmployeeId: '',
      allEmployees: [],
      persnrError: '',
      persnrPrefix: 'CSP',
      // Fremdfirma (Subunternehmer) lookup
      kontakte: [],
      // Einsatzort-Sperren
      einsatzortBlocks: [],
      blockForm: { contractCode: '', einsatzortCode: '', reason: '' },
      blockContracts: [],
      blockSaving: false,
      blockError: '',
      approvingEmployee: false
    };
  },

  computed: {
    requiredDocuments() {
      return [
        { field: 'ihk', subcategory: 'IHK-Bescheinigung', label: 'IHK-Bescheinigung' },
        { field: 'bewacherpdf', subcategory: 'Bewacher-PDF', label: 'Bewacher-PDF' },
        {
          field: 'aufenthaltsnachweis',
          subcategory: 'Aufenthaltsnachweis',
          label: 'Aufenthaltserlaubnis',
          manualDateField: 'aufenthaltsnachweisManualValidUntil'
        },
        {
          field: 'personalausweis',
          subcategory: 'Personalausweis',
          label: 'Personalausweis',
          manualDateField: 'personalausweisManualValidUntil'
        },
        { field: 'svNachweis', subcategory: 'SV-Nachweis', label: 'SV-Nachweis' }
      ];
    },
    lohnFiles() {
      return this.files.filter(f => f.category === 'Lohn');
    },
    personalakteFiles() {
      return this.files.filter(f => f.category === 'Personalakte');
    },
    qualifikationFiles() {
      return this.files.filter(f => f.category === 'Qualifikation');
    },
    vacationStats() {
      const currentYear = new Date().getFullYear();
      const march31 = new Date(currentYear, 2, 31);
      const today = new Date();

      let vacationFromLastYearTaken = 0;
      let regularVacationTaken = 0;
      let plannedDays = 0;

      this.vacationDays.forEach(day => {
        if (day.isFuture) {
          plannedDays += 1;
        } else if (day.isBeforeMarch31 && this.localUser.vacationDaysLastYear > vacationFromLastYearTaken) {
          vacationFromLastYearTaken += 1;
        } else {
          regularVacationTaken += 1;
        }
      });

      const totalAvailable = (this.localUser.vacationDaysThisYear || 0) + (this.localUser.vacationDaysLastYear || 0);
      const vacationDaysTakenOutside = Number(this.localUser.vacationDaysTakenOutside) || 0;
      const totalTaken = vacationFromLastYearTaken + regularVacationTaken + vacationDaysTakenOutside;
      const remainingDays = totalAvailable - totalTaken;

      return {
        vacationFromLastYearTaken: vacationFromLastYearTaken.toFixed(1),
        regularVacationTaken: regularVacationTaken.toFixed(1),
        vacationTakenOutside: vacationDaysTakenOutside.toFixed(1),
        plannedDays: plannedDays.toFixed(1),
        remainingDays: remainingDays.toFixed(1)
      };
    },
    isAdmin() {
      return Boolean(this.sessionStats?.permissions?.admin);
    },
    isSuperadmin() {
      return Boolean(this.sessionStats?.permissions?.superadmin);
    },
    isExternalDispo() {
      const permissions = this.sessionStats?.permissions || {};
      return Boolean(permissions.externalDispo && !permissions.admin);
    },
    ownCa() {
      return this.sessionStats?.user?.ca || '';
    },
    firmPersnrPrefix() {
      const subcontractor = this.subunternehmer.find(k => k.firmenkuerzel === (this.isExternalDispo ? this.ownCa : this.localUser.Firmenkurzel));
      return subcontractor?.persnrPrefix || '';
    },
    blockLocations() {
      const contract = this.blockContracts.find(c => c.code === this.blockForm.contractCode);
      if (!contract) return [];
      return contract.locations || [];
    },
    subunternehmer() {
      return this.kontakte.filter(k => k.is_subunternehmer && k.firmenkuerzel);
    },
    matchedFremdfirma() {
      if (!this.localUser.Firmenkurzel) return null;
      return this.subunternehmer.find(k => k.firmenkuerzel === this.localUser.Firmenkurzel) || null;
    },
    fremdfirmaWarning() {
      if (!this.localUser.is_external || this.matchedFremdfirma) return '';
      if (this.localUser.Firmenkurzel) {
        return `Kein Kreditor mit dem Firmenkürzel „${this.localUser.Firmenkurzel}“ gefunden.`;
      }
      return 'Bitte eine Fremdfirma auswählen.';
    },
    einsatzfaehig() {
      return isEmployeeEinsatzfaehig({
        employee: this.localUser,
        creditor: this.matchedFremdfirma,
        files: this.files,
        today: this.berlinToday()
      });
    },
  },
  methods: {
    formatLastSeen(value) {
      if (!value) return 'Noch nicht erfasst';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Noch nicht erfasst';
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    calculateHours(job) {
      if (!job.Von_Uhrzeit || !job.Bis_Uhrzeit || !job.Datum_von || !job.Datum_bis) {
        return '0.00';
      }

      // Parse times
      const vonParts = job.Von_Uhrzeit.split(':');
      const bisParts = job.Bis_Uhrzeit.split(':');
      const vonHours = parseInt(vonParts[0]);
      const vonMinutes = parseInt(vonParts[1]);
      const bisHours = parseInt(bisParts[0]);
      const bisMinutes = parseInt(bisParts[1]);

      // Parse dates
      const vonDate = new Date(job.Datum_von);
      const bisDate = new Date(job.Datum_bis);

      // Calculate start and end timestamps in minutes
      const startMinutes = vonDate.getTime() / (1000 * 60) + (vonHours * 60 + vonMinutes);
      const endMinutes = bisDate.getTime() / (1000 * 60) + (bisHours * 60 + bisMinutes);

      // Calculate duration in minutes
      const totalMinutes = endMinutes - startMinutes;

      // Convert to hours and format
      if (totalMinutes > 0) {
        return (totalMinutes / 60).toFixed(2);
      }

      return '0.00';
    },
    openMaterialTab() {
      this.activeTab = 'Material';
    },
    openBlocksTab() {
      this.activeTab = 'Sperrungen';
      this.loadBlocks();
      this.loadBlockContracts();
    },
    loadBlockContracts() {
      if (this.blockContracts.length) return;
      CASP.getPresets(this.token, (res) => {
        if (res && Array.isArray(res.contracts)) {
          this.blockContracts = res.contracts.filter(contract => contract.code);
        }
      });
    },
    loadBlocks() {
      if (!this.localUser.id) return;
      CASP.getEinsatzortBlocks(this.localUser.id, (status, response) => {
        if (status === 200 && Array.isArray(response)) {
          this.einsatzortBlocks = response;
        }
      });
    },
    createBlock() {
      if (!this.blockForm.einsatzortCode) return;
      this.blockSaving = true;
      this.blockError = '';
      CASP.createEinsatzortBlock(this.localUser.id, this.blockForm, (status, response) => {
        this.blockSaving = false;
        // postWithStatus leaves non-200 bodies unparsed — 201/4xx arrive as strings.
        if (typeof response === 'string') {
          try { response = JSON.parse(response); } catch (e) { /* keep raw body */ }
        }
        if (status === 201) {
          this.einsatzortBlocks.unshift(response);
          this.blockForm = { contractCode: '', einsatzortCode: '', reason: '' };
        } else {
          this.blockError = response?.desc || 'Sperre konnte nicht angelegt werden.';
        }
      });
    },
    deleteBlock(block) {
      if (!confirm('Sperre wirklich entfernen?')) return;
      CASP.deleteEinsatzortBlock(block.id, (status) => {
        if (status === 200) {
          this.einsatzortBlocks = this.einsatzortBlocks.filter(b => b.id !== block.id);
        }
      });
    },
    approveEmployee() {
      this.approvingEmployee = true;
      CASP.approveEmployee(this.localUser.id, (status) => {
        this.approvingEmployee = false;
        if (status === 200) {
          this.localUser.pendingApproval = false;
          this.localUser.pendingApprovalBy = null;
          this.$emit('updateUser', this.localUser);
        } else {
          alert('Freigabe fehlgeschlagen.');
        }
      });
    },
    onMaterialEdit(idx) {
      this.materials[idx].editDirty = (this.materials[idx].editName !== this.materials[idx].name);
    },
    getMaterial() {
      CASP.getEquipment((status, response) => {
        if (status === 200) {
          this.equipment = response;
        }
      });
    },
    saveMaterial(idx) {
      const mat = this.materials[idx];
      const updated = { ...mat, name: mat.editName };
      CASP.updateMaterial(updated, (status, res) => {
        if (status === 200) {
          this.materials[idx].name = this.materials[idx].editName;
          this.materials[idx].editDirty = false;
        } else {
          alert('Fehler beim Speichern');
        }
      });
    },
    deleteMaterial(idx) {
      const mat = this.materials[idx];
      if (confirm('Material wirklich löschen?')) {
        CASP.deleteMaterial(mat.id, (status, res) => {
          if (status === 200) {
            this.materials.splice(idx, 1);
          } else {
            alert('Fehler beim Löschen');
          }
        });
      }
    },
    addMaterial() {
      const name = this.newMaterialName.trim();
      if (!name) return;
      CASP.addMaterial(this.localUser.id, { name: name }, (status, res) => {
        if (status === 200 && res && res.id) {
          this.materials.push({
            id: res.id,
            name: res.name,
            editName: res.name,
            editDirty: false
          });
          this.newMaterialName = '';
        } else {
          alert('Fehler beim Hinzufügen');
        }
      });
    },
    deleteDocument(doc) {
      CASP.deleteUserDocument(doc.id, (status, response) => {
        if (status == 200) {
          this.files = this.files.filter(f => f.id !== doc.id);
        }
      })
    },
    requiredDocumentFiles(document) {
      return this.files.filter(file =>
        file.category === 'Qualifikation' &&
        file.subcategory === document.subcategory &&
        !file.archived
      );
    },
    requiredDocumentFile(document) {
      const files = this.requiredDocumentFiles(document);
      return files.find(file => !this.isRequiredDocumentExpired(file)) || null;
    },
    shouldShowManualDate(document) {
      return Boolean(
        document.manualDateField &&
        this.localUser[document.field] &&
        !this.requiredDocumentFiles(document).length &&
        !(document.subcategory === 'Aufenthaltsnachweis' && this.localUser.germanNational)
      );
    },
    requiredDocumentManualDateError() {
      const document = this.requiredDocuments.find(requiredDocument =>
        this.shouldShowManualDate(requiredDocument) &&
        !this.localUser[requiredDocument.manualDateField]
      );
      return document
        ? `Gültigkeitsdatum für ${document.label} fehlt.`
        : '';
    },
    validateRequiredDocumentManualDates() {
      this.documentStatusError = this.requiredDocumentManualDateError();
      return !this.documentStatusError;
    },
    handleRequiredDocumentConfirmation(document) {
      if (!this.localUser[document.field] && document.manualDateField) {
        this.localUser[document.manualDateField] = null;
      }
    },
    berlinToday() {
      return new Intl.DateTimeFormat('sv-SE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());
    },
    isRequiredDocumentExpired(file) {
      return isExpired(file, this.berlinToday());
    },
    requiredDocumentStatus(document) {
      const files = this.requiredDocumentFiles(document);
      return getRequiredDocumentStatus({
        subcategory: document.subcategory,
        files,
        germanNational: this.localUser.germanNational,
        confirmed: Boolean(this.localUser[document.field]),
        manualValidUntil: document.manualDateField
          ? this.localUser[document.manualDateField]
          : null,
        today: this.berlinToday()
      });
    },
    saveRequiredDocumentStatus(done) {
      if (!this.validateRequiredDocumentManualDates()) {
        if (done) done(false);
        return;
      }
      if (this.localUser.isNew || !this.localUser.id) {
        if (done) done(true);
        return;
      }
      this.documentStatusSaving = true;
      this.documentStatusMessage = '';
      this.documentStatusError = '';
      const status = {
        germanNational: Boolean(this.localUser.germanNational),
        ihk: Boolean(this.localUser.ihk),
        bewacherpdf: Boolean(this.localUser.bewacherpdf),
        aufenthaltsnachweis: Boolean(this.localUser.aufenthaltsnachweis),
        personalausweis: Boolean(this.localUser.personalausweis),
        svNachweis: Boolean(this.localUser.svNachweis),
        aufenthaltsnachweisManualValidUntil: this.localUser.aufenthaltsnachweis
          ? this.localUser.aufenthaltsnachweisManualValidUntil || null
          : null,
        personalausweisManualValidUntil: this.localUser.personalausweis
          ? this.localUser.personalausweisManualValidUntil || null
          : null
      };
      CASP.updateUser(this.localUser.id, status, (responseStatus) => {
        this.documentStatusSaving = false;
        if (responseStatus === 200) {
          this.documentStatusMessage = 'Dokumentenstatus gespeichert';
          if (done) done(true);
        } else {
          this.documentStatusError = 'Dokumentenstatus konnte nicht gespeichert werden';
          if (done) done(false);
        }
      });
    },
    openDocumentUpload() {
      this.openUploadAfterStatusSave('');
    },
    closeDocumentUpload() {
      this.showDocumentUploadModal = false;
      this.documentUploadSubcategory = '';
    },
    openRequiredDocumentUpload(subcategory) {
      if (this.localUser.isNew || !this.localUser.id) return;
      this.openUploadAfterStatusSave(subcategory);
    },
    openUploadAfterStatusSave(subcategory) {
      this.saveRequiredDocumentStatus(saved => {
        if (!saved) return;
        this.documentUploadSubcategory = subcategory;
        this.showDocumentUploadModal = true;
      });
    },
    viewJobDocument(document) {
      window.open("/jobs/" + document.job + "/documents/" + document.id, '_blank');
    },
    viewDocument(doc) {
      CASP.getFileOTL(doc.id, (status, res) => {
        if (status === 200 && res && res.link) {
          window.open(process.env.VUE_APP_API_URL + res.link, '_blank');
        } else {
          alert('Fehler beim Laden des Dokuments');
        }
      });
    },
    updateUserData() {
      this.documentUploadSubcategory = '';
      this.getUser(this.user.id);
    },
    deleteTrainingExecution(execution) {
      CASP.deleteTrainingExecution(execution.id, (status, response) => {
        if (status == 200) {
          this.localUser.trainings = this.localUser.trainings.filter(t => t.id !== execution.id);
        }
      })
    },
    saveUser() {
      if (!this.validateRequiredDocumentManualDates()) {
        alert(this.documentStatusError);
        return;
      }
      if (this.localUser.isNew) {
        this.validatePersnr();
        if (this.persnrError) {
          alert('Personalnummer ist ungültig oder bereits vergeben.');
          return;
        }
      }
      document.getElementById("mainspinner").style.display = "inline-block";
      
      if (this.localUser.isNew) {
        // Create new employee
        CASP.createUser(this.localUser, (status, response) => {
          document.getElementById("mainspinner").style.display = "none";
          if (status == 200 || status == 201) {
            this.$emit('close');
            this.$emit('updateUser', response);
          } else {
            alert('Fehler beim Erstellen des Mitarbeiters');
          }
        });
      } else {
        // Update existing employee
        if (this.pictureChanged) {
          CASP.savePicture(
            this.token,
            this.localUser,
            {
              data: this.localUser.picture.data.split(',')[1],
              fileName: this.localUser.pictureName,
            },
            (res) => {
              this.localUser.pictureId = res.id;
              this.localUser.pictureName = this.localUser.pictureName;
              this.pictureChanged = false;
            }
          );
        }
        const userUpdate = {
          ...this.localUser,
          activeManuallyChanged: this.activeManuallyChanged
        };
        CASP.updateUser(this.localUser.id, userUpdate, (status) => {
          document.getElementById("mainspinner").style.display = "none";
          if (status == 200) {
            this.$emit('close');
            this.$emit('updateUser', this.localUser);
          } else {
            alert('Fehler beim Speichern des Mitarbeiters');
          }
        });
      }
    },
    printBadge() {
      if (!this.localUser?.id) return;
      const resolved = this.$router.resolve({
        name: 'BadgePrint',
        params: { userId: this.localUser.id }
      });
      window.open(resolved.href, '_blank', 'noopener');
    },
    getUser(userId) {
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.getUser(userId, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status == 200) {
          this.localUser = response;
          this.files = response.files || [];
          this.materials = response.materials || [];
          this.materials.forEach(mat => {
            mat.editName = mat.name;
            mat.editDirty = false;
          });
          this.assignedDocuments = response.assignedDocuments || [];
        }
      });
    },
    triggerProfilePicInput() {
      this.$refs.profilePicInput && this.$refs.profilePicInput.click();
    },
    uploadPicture() {
      // Deprecated, kept for compatibility
    },
    handlePictureUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.localUser.picture = { data: e.target.result };
          this.localUser.pictureName = file.name;
          this.pictureChanged = true;
          // Auto-save the picture for existing users (new users are saved via "Alles speichern")
          if (!this.localUser.isNew && this.localUser.id) {
            this.savePictureOnly();
          }
        };
        reader.readAsDataURL(file);
      }
    },
    savePictureOnly() {
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.savePicture(
        this.token,
        this.localUser,
        {
          data: this.localUser.picture.data.split(',')[1],
          fileName: this.localUser.pictureName,
        },
        (res) => {
          document.getElementById("mainspinner").style.display = "none";
          this.localUser.pictureId = res.id;
          this.pictureChanged = false;
          this.showSuccessMessage('Profilbild gespeichert');
        }
      );
    },
    formatLohnFilename(filename) {
      // Format: <NR>_<YEAR>-<MonthOfYear>
      if (!filename) return '';
      const parts = filename.split('_');
      if (parts.length < 2) return filename;
      let dateparts = parts[1].split('-');
      const year = dateparts[0];
      const monthNum = parseInt(dateparts[1]);
      const months = [
        '', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ];
      const month = months[monthNum] || dateparts[1];
      return `${month} ${year}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString();
    },
    showSuccessMessage(message) {
      this.successMessage = message;
      setTimeout(() => {
        this.successMessage = '';
      }, 3000);
    },
    openVacationTab() {
      this.activeTab = 'Urlaubsplanung';
      this.loadVacationData();
    },
    loadVacationData() {
      if (!this.localUser.persnr) return;

      document.getElementById("mainspinner").style.display = "inline-block";
      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;

      // Load current year vacation data
      const currentYearFilter = {
        Mitarbeiternr: this.localUser.persnr,
        start: `${currentYear}-01-01`,
        ende: `${currentYear}-12-31`,
        Funktion: 'Urlaub Bezahlt'
      };

      // Load previous year jobs for selection
      const previousYearFilter = {
        Mitarbeiternr: this.localUser.persnr,
        start: `${previousYear}-01-01`,
        ende: `${previousYear}-12-31`,
        Funktion: 'Urlaub Bezahlt'
      };
      let vm = this
      // Load current year vacation data
      CASP.getAdminJobs('', currentYearFilter, (response) => {
        if (response) {
          this.vacationBalanceChanged = false;
          const vacationDays = response.jobs
            .filter(job => {
              const jobDate = new Date(job.Datum_von);
              return jobDate.getFullYear() === currentYear;
            })
            .map(job => ({
              id: job.id,
              date: job.Datum_von.split("T")[0],
              startTime: job.Soll_Von ? job.Soll_Von.substring(0, 5) : '',
              endTime: job.Soll_Bis ? job.Soll_Bis.substring(0, 5) : '',
              hours: vm.calculateHours(job)
            }));
          this.processVacationDays(vacationDays);
        }

        // Load previous year jobs
        CASP.getAdminJobs('', previousYearFilter, (prevYearResponse) => {
          document.getElementById("mainspinner").style.display = "none";
          if (prevYearResponse) {
            this.previousYearJobs = prevYearResponse.jobs
              .filter(job => {
                const jobDate = new Date(job.Datum_von);
                return jobDate.getFullYear() === previousYear;
              })
              .map(job => ({
                id: job.id,
                date: job.Datum_von.split("T")[0],
                startTime: job.Soll_Von ? job.Soll_Von.substring(0, 5) : '',
                endTime: job.Soll_Bis ? job.Soll_Bis.substring(0, 5) : '',
                hours: vm.calculateHours(job),
                description: `Urlaub vom ${this.formatDate(job.Datum_von.split("T")[0])}`,
                isPreviousYear: true
              }));
          }
        });
      });
    },
    processVacationDays(days) {
      const currentYear = new Date().getFullYear();
      const march31 = new Date(currentYear, 2, 31);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      this.vacationDays = days.map(day => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);

        return {
          ...day,
          isFuture: dayDate > today,
          isBeforeMarch31: dayDate <= march31 && dayDate <= today
        };
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    saveVacationBalance() {
      if (!this.vacationBalanceChanged) return;

      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.updateUser(this.localUser.id, this.localUser, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status === 200) {
          this.vacationBalanceChanged = false;
          this.showSuccessMessage('Urlaubsstand erfolgreich gespeichert');
        } else {
          this.showSuccessMessage('Fehler beim Speichern des Urlaubsstands');
        }
      });
    },
    calculateHoursForJob(jobId) {
      document.getElementById("mainspinner").style.display = "inline-block";

      CASP.calculateVacationHours(jobId, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";

        if (status === 200 && response && response.success) {
          this.showSuccessMessage('Urlaubsstunden erfolgreich berechnet und aktualisiert');
          // Reload the vacation data to show updated times
          this.loadVacationData();
        } else {
          this.showSuccessMessage('Fehler beim Berechnen der Urlaubsstunden: ' + (response?.message || 'Unbekannter Fehler'));
        }
      });
    },
    addVacation() {
      if (!this.newVacationDate) {
        this.showSuccessMessage('Bitte wählen Sie ein Datum aus');
        return;
      }

      const date = new Date(this.newVacationDate);
      const dayOfWeek = ['SO', 'MO', 'DI', 'MI', 'DO', 'FR', 'SA'][date.getDay()];

      const vacationJob = {
        "Lfd_Nr": "UUB",
        "Vertragsbeschreibung": "Interne Büro Stunden",
        "Zeiterf_Buch_Blattname": "GLOBAL",
        "Von_Uhrzeit": "",
        "Bis_Uhrzeit": "",
        "Pos": "Urlaub Bezahlt",
        "Dispo": true,
        "Tarifvertrag_Code": "SECURITY TARIF 2024",
        "Rubrikencode": "CASP OFFICE",
        "Debitornr": "10000-2",
        "Debitorname": "CASP Security GmbH",
        "Halle": "",
        "Ebene": "0",
        "Gang": "",
        "Stand": "",
        "Kundenname": "",
        "Kundennr": "",
        "Funktion": "Urlaub Bezahlt",
        "Ausweisnummer": "",
        "MA_Info": false,
        "MA_gebucht": false,
        "MA_bestätigt": false,
        "VA_Bewachung": false,
        "VA_Normal": false,
        "VA_Top": false,
        "checkedIn": "",
        "checkinConfirmed": false,
        "checkinRequired": false,
        "Einsatzstelle_Beschreibung": "Urlaub Bezahlt",
        "Einsatzort_Beschreibung": "CASP Office Frechen",
        "Treffpunkt": "Büro Frechen",
        "Bemerkungen": "Urlaub",
        "Mitarbeiternr": this.localUser.persnr,
        "SUB": false,
        "Q": "",
        "Bez": false,
        "Firmenkurzel": "CASP",
        "Ist_Zeiten_geprüft": false,
        "Datum_von": this.newVacationDate,
        "Datum_bis": this.newVacationDate,
        "datum_formatted": date.toLocaleDateString('de-DE'),
        "Soll_Von": "09:00",
        "Soll_Bis": "10:00",
        "T_N": "T",
        "WT": dayOfWeek,
        "Belegnr": "V-00043",
        "Leistungsart_Code": "UB",
        "Einsatzort_Code": "O-10430",
        "Einsatzstelle_Code": "URLAUB1001",
        "Zähler_Anzahl": "1"
      };

      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.createJob('', vacationJob, (response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (response) {
          this.showSuccessMessage('Urlaub eingetragen');
          this.newVacationDate = '';
          this.loadVacationData();
        } else {
          this.showSuccessMessage('Fehler beim Eintragen des Urlaubs');
        }
      });
    },
    openTimeAccountTab() {
      this.activeTab = 'Zeitkonto';
    },
    async copyEmployeeData() {
      if (!this.copyFromEmployeeId) return;
      
      document.getElementById("mainspinner").style.display = "inline-block";
      CASP.getUser(this.copyFromEmployeeId, (status, response) => {
        document.getElementById("mainspinner").style.display = "none";
        if (status == 200) {
          const sourceEmployee = response;
          
          
          
          // Copy relevant fields
          this.localUser = {
            ...this.localUser,
            isNew: true,
            persnr: this.getNextFreePersnr(this.persnrPrefix),
            name: sourceEmployee.name || '',
            surname: sourceEmployee.surname || '',
            street: sourceEmployee.street || '',
            zip: sourceEmployee.zip || '',
            city: sourceEmployee.city || '',
            phone: sourceEmployee.phone || '',
            email: sourceEmployee.email || '',
            Qualifikation: sourceEmployee.Qualifikation,
            function: sourceEmployee.function || '',
            idbewar: sourceEmployee.idbewar || '',
            Ausweisnr: '',
            qrValue: sourceEmployee.qrValue || 'https://casp-security.com/',
            monthlyHours: sourceEmployee.monthlyHours || '',
            minijob: sourceEmployee.minijob || false,
            vacationDaysThisYear: sourceEmployee.vacationDaysThisYear || 26,
            vacationDaysLastYear: 0,
            vacationDaysTakenOutside: 0,
            active: false,
            kennzeichen: sourceEmployee.kennzeichen || ''
          };
        }
      });
    },
    getAllEmployees() {
      CASP.getAllUsers((res) => {
        if (res) {
          this.allEmployees = res.sort((a, b) => (a.persnr || '').localeCompare(b.persnr || ''));
          // Ersetzt den initialen max()+1-Vorschlag durch die erste freie Nummer,
          // sofern der Admin das Feld noch nicht manuell geändert hat
          if (this.localUser.isNew && this.localUser.persnr === (this.user.persnr || '')) {
            this.applyPersnrSuggestion();
          }
        }
      });
    },
    loadKontakte() {
      CASP.getKontakte(null, "", (status, response) => {
        if (status === 200) {
          this.kontakte = response;
        } else {
          console.error('Fehler beim Laden der Kontakte:', response);
        }
      });
    },
    validatePersnr() {
      if (!this.localUser.isNew && !this.isSuperadmin) return;
      const persnr = (this.localUser.persnr || '').trim();
      if (!persnr) {
        this.persnrError = 'Personalnummer darf nicht leer sein.';
        return;
      }
      const prefixMatch = persnr.match(/^(CSP|SUB)/i);
      if (prefixMatch) this.persnrPrefix = prefixMatch[1].toUpperCase();
      const taken = this.allEmployees.some(
        (el) => el.id !== this.localUser.id && el.persnr && el.persnr.toLowerCase() === persnr.toLowerCase()
      );
      this.persnrError = taken ? 'Diese Personalnummer ist bereits vergeben.' : '';
      if (!this.persnrError && this.isExternalDispo && this.firmPersnrPrefix &&
        !persnr.toUpperCase().startsWith(this.firmPersnrPrefix.toUpperCase())) {
        this.persnrError = `Personalnummer muss mit „${this.firmPersnrPrefix}" beginnen.`;
      }
    },
    getNextFreePersnr(prefix) {
      // Kleinste freie Nummer >= 10001; Lücken werden aufgefüllt.
      // allEmployees enthält auch inaktive Mitarbeiter, deren Nummern vergeben bleiben.
      const taken = new Set();
      const pattern = new RegExp('^' + prefix + '(\\d+)$', 'i');
      this.allEmployees.forEach((el) => {
        const match = el.persnr && el.persnr.match(pattern);
        if (match) taken.add(parseInt(match[1], 10));
      });
      let number = 10001;
      while (taken.has(number)) number++;
      return prefix + number;
    },
    applyPersnrSuggestion() {
      if (this.persnrPrefix === 'SUB') {
        this.applySubPersnrSuggestion();
        return;
      }
      this.localUser.persnr = this.getNextFreePersnr(this.persnrPrefix);
      this.validatePersnr();
    },
    applySubPersnrSuggestion() {
      const firmenkuerzel = this.isExternalDispo ? this.ownCa : this.localUser.Firmenkurzel;
      if (!firmenkuerzel) {
        this.localUser.persnr = '';
        this.persnrError = 'Bitte zuerst eine Fremdfirma auswählen.';
        return;
      }
      CASP.getNextPersnr(firmenkuerzel, (status, response) => {
        if (status === 200 && response?.persnr) {
          this.localUser.persnr = response.persnr;
          this.validatePersnr();
        } else {
          this.localUser.persnr = '';
          this.persnrError = response?.desc || 'Kein Personalnummern-Präfix für diese Fremdfirma hinterlegt.';
        }
      });
    },
    updateActiveStatus() {
      this.activeManuallyChanged = false;

      const today = new Date();
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const exitDateValue = this.localUser.exitDate;
      const exitDate = exitDateValue
        ? new Date(`${exitDateValue}T00:00:00`)
        : null;
      const shouldBeActive = !exitDateValue ||
        (exitDate && !Number.isNaN(exitDate.getTime()) && exitDate > todayDate);

      if (this.localUser.active !== shouldBeActive) {
        this.localUser.active = shouldBeActive;
        this.activeAutoUpdated = true;
        setTimeout(() => {
          this.activeAutoUpdated = false;
        }, 5000);
      }
    }
  },
  watch: {
    'localUser.Firmenkurzel'(value) {
      // Admins get a SUB suggestion tied to the selected Fremdfirma.
      if (this.localUser.isNew && !this.isExternalDispo && this.persnrPrefix === 'SUB' && value) {
        this.applySubPersnrSuggestion();
      }
    }
  },
  mounted() {
    this.token = this.cookies.get("caspauth") || this.cookies.get("authtoken");
    this.loadKontakte();
    if (this.user.isNew) {
      this.localUser = { ...this.user };
      if (this.isExternalDispo) {
        this.localUser.is_external = true;
        this.localUser.Firmenkurzel = this.ownCa;
        this.persnrPrefix = 'SUB';
        this.applySubPersnrSuggestion();
      }
      this.getAllEmployees();
      this.getMaterial();
    } else {
      this.getUser(this.user.id);
      this.getMaterial();
    }
  }

}
</script>

<style scoped></style>
